import { useCallback, useEffect, useRef, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import RefreshButton from "../../components/admin/RefreshButton";
import { useAdminToast } from "../../context/AdminToastContext";
import { parseApiError } from "../../lib/http/apiErrors";
import { formatISTDateTime } from "../../utils/dateUtils";
import InvoiceLogoCard from "./InvoiceLogoCard";
import {
  activateInvoiceTemplateVersion,
  fetchInvoiceTemplateVersions,
  fetchInvoiceTemplates,
  previewInvoiceTemplate,
  resetInvoiceTemplate,
  saveInvoiceTemplate,
  type InvoiceTemplateVersion,
  type InvoiceTemplatesResponse,
  type TemplateDocKey,
  type TemplateValidationError,
} from "../../features/admin/billingApi";

/** Billing → Invoice design: edit the HTML/CSS used to render the tax invoice
 *  and payment receipt PDFs. See backend/app/modules/invoices/admin_routes.py
 *  (templates endpoints) and pdf.py (renderer + the GST-field validation that
 *  backs `errors`). */

const input = "w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500";

const DOC_LABEL: Record<TemplateDocKey, string> = {
  invoice: "Tax invoice",
  receipt: "Payment receipt",
};

function describeError(err: unknown): TemplateValidationError {
  const e = err as Partial<TemplateValidationError>;
  if (e && typeof e.message === "string" && Array.isArray(e.errors)) {
    return e as TemplateValidationError;
  }
  const message = parseApiError(err).message;
  return Object.assign(new Error(message), { errors: [] as string[] });
}

export default function AdminInvoiceDesignPage() {
  const { showError, showSuccess } = useAdminToast();

  const [activeDoc, setActiveDoc] = useState<TemplateDocKey>("invoice");
  const [data, setData] = useState<InvoiceTemplatesResponse | null>(null);
  const [versions, setVersions] = useState<Record<TemplateDocKey, InvoiceTemplateVersion[]>>({ invoice: [], receipt: [] });
  const [loading, setLoading] = useState(true);

  const [html, setHtml] = useState("");
  const [savedHtml, setSavedHtml] = useState("");

  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState<number | null>(null);
  const [resetting, setResetting] = useState(false);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewErrors, setPreviewErrors] = useState<string[]>([]);
  const [previewWarnings, setPreviewWarnings] = useState<string[]>([]);
  const [previewInvoiceId, setPreviewInvoiceId] = useState("");

  const [placeholdersOpen, setPlaceholdersOpen] = useState(false);
  const [copiedPlaceholder, setCopiedPlaceholder] = useState<string | null>(null);

  const objectUrlRef = useRef<string | null>(null);
  const isDirty = html !== savedHtml;

  const clearPreview = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setPreviewUrl(null);
    setPreviewErrors([]);
    setPreviewWarnings([]);
  }, []);

  const loadTemplates = useCallback(async (): Promise<InvoiceTemplatesResponse | null> => {
    try {
      const res = await fetchInvoiceTemplates();
      setData(res);
      return res;
    } catch (err) {
      showError(parseApiError(err).message);
      return null;
    }
  }, [showError]);

  const loadVersions = useCallback(async (doc: TemplateDocKey) => {
    try {
      const res = await fetchInvoiceTemplateVersions(doc);
      setVersions((v) => ({ ...v, [doc]: res.versions }));
    } catch (err) {
      showError(parseApiError(err).message);
    }
  }, [showError]);

  const refreshAll = useCallback(async () => {
    const res = await loadTemplates();
    if (res) {
      const docHtml = res.docs[activeDoc].html;
      setHtml(docHtml);
      setSavedHtml(docHtml);
    }
    await loadVersions(activeDoc);
  }, [activeDoc, loadTemplates, loadVersions]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await loadTemplates();
      if (res) {
        setHtml(res.docs.invoice.html);
        setSavedHtml(res.docs.invoice.html);
      }
      await loadVersions("invoice");
      setLoading(false);
    })();
    // Runs once on mount; tab switches are handled by switchDoc below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => clearPreview(), [clearPreview]);

  const switchDoc = (doc: TemplateDocKey) => {
    if (doc === activeDoc) return;
    if (isDirty && !window.confirm("Discard unsaved changes to the editor?")) return;
    setActiveDoc(doc);
    clearPreview();
    const docHtml = data?.docs[doc].html ?? "";
    setHtml(docHtml);
    setSavedHtml(docHtml);
    if (!versions[doc].length) void loadVersions(doc);
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const { blob, errors, warnings } = await previewInvoiceTemplate(activeDoc, {
        html,
        invoice_id: previewInvoiceId.trim() || undefined,
      });
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      setPreviewUrl(url);
      setPreviewErrors(errors);
      setPreviewWarnings(warnings);
    } catch (err) {
      const e = describeError(err);
      setPreviewUrl((prev) => {
        if (prev && objectUrlRef.current === prev) {
          URL.revokeObjectURL(prev);
          objectUrlRef.current = null;
        }
        return null;
      });
      setPreviewErrors(e.errors.length ? e.errors : [e.message]);
      setPreviewWarnings([]);
      showError(e.message);
    } finally {
      setPreviewing(false);
    }
  };

  const handleSave = async () => {
    const note = window.prompt("Note for this version (optional):", "");
    if (note === null) return;
    setSaving(true);
    try {
      const res = await saveInvoiceTemplate(activeDoc, { html, note: note.trim() || undefined });
      showSuccess(`Saved as version ${res.saved.version}.`);
      setSavedHtml(html);
      await loadTemplates();
      await loadVersions(activeDoc);
    } catch (err) {
      showError(describeError(err).message);
    } finally {
      setSaving(false);
    }
  };

  const handleLoadBuiltIn = () => {
    if (!data) return;
    setHtml(data.default_html);
  };

  const handleReset = async () => {
    if (!window.confirm(`Reset the ${DOC_LABEL[activeDoc]} template to the built-in Gullivr design? This cannot be undone.`)) return;
    setResetting(true);
    try {
      await resetInvoiceTemplate(activeDoc);
      showSuccess("Reset to the built-in design.");
      const res = await loadTemplates();
      if (res) {
        const docHtml = res.docs[activeDoc].html;
        setHtml(docHtml);
        setSavedHtml(docHtml);
      }
      await loadVersions(activeDoc);
      clearPreview();
    } catch (err) {
      showError(parseApiError(err).message);
    } finally {
      setResetting(false);
    }
  };

  const handleDiscard = () => setHtml(savedHtml);

  const handleLoadVersion = (v: InvoiceTemplateVersion) => {
    if (isDirty && !window.confirm("Discard unsaved changes to the editor?")) return;
    setHtml(v.html);
  };

  const handleMakeActive = async (v: InvoiceTemplateVersion) => {
    if (!window.confirm(`Make version ${v.version} the active ${DOC_LABEL[activeDoc].toLowerCase()} template?`)) return;
    setActivating(v.version);
    try {
      await activateInvoiceTemplateVersion(activeDoc, v.version);
      showSuccess(`Version ${v.version} is now active.`);
      const res = await loadTemplates();
      if (res) {
        const docHtml = res.docs[activeDoc].html;
        setHtml(docHtml);
        setSavedHtml(docHtml);
      }
      await loadVersions(activeDoc);
    } catch (err) {
      showError(describeError(err).message);
    } finally {
      setActivating(null);
    }
  };

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Tab") return;
    e.preventDefault();
    const el = e.currentTarget;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const next = html.slice(0, start) + "  " + html.slice(end);
    setHtml(next);
    requestAnimationFrame(() => {
      el.selectionStart = el.selectionEnd = start + 2;
    });
  };

  const copyPlaceholder = async (placeholder: string) => {
    try {
      await navigator.clipboard.writeText(placeholder);
      setCopiedPlaceholder(placeholder);
      setTimeout(() => setCopiedPlaceholder((p) => (p === placeholder ? null : p)), 1500);
    } catch {
      showError("Couldn't copy — select and copy manually.");
    }
  };

  const docInfo = data?.docs[activeDoc] ?? null;
  const activeVersionMeta = docInfo?.active_version != null
    ? versions[activeDoc].find((v) => v.version === docInfo.active_version)
    : undefined;
  const statusText = !docInfo
    ? ""
    : docInfo.is_default || docInfo.active_version == null
      ? "Using: built-in Gullivr design"
      : activeVersionMeta
        ? `Using: version ${activeVersionMeta.version} (saved by ${activeVersionMeta.created_by} on ${formatISTDateTime(activeVersionMeta.created_at)})`
        : `Using: version ${docInfo.active_version}`;

  const sizeBytes = new Blob([html]).size;
  const sizeKb = (sizeBytes / 1024).toFixed(1);
  const maxKb = data ? (data.max_bytes / 1024).toFixed(0) : null;
  const overLimit = data ? sizeBytes > data.max_bytes : false;

  const versionList = versions[activeDoc];

  return (
    <div>
      <AdminPageHeader
        title="Invoice design"
        subtitle="Customize the HTML/CSS used to render invoice and receipt PDFs."
        actions={<RefreshButton onRefresh={refreshAll} />}
      />

      <InvoiceLogoCard />

      <p className="mb-6 max-w-3xl text-sm text-gray-500">
        Paste your HTML (CSS inside <code>&lt;style&gt;</code>). Use the placeholders below; customer data is filled
        in automatically. Images: use <code>{"{{ brand.logo }}"}</code>, a <code>data:</code> URI, or a public
        <code> https://</code> link. Saving checks that GST-required fields are present.
      </p>

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {(["invoice", "receipt"] as const).map((doc) => (
            <button key={doc} type="button" onClick={() => switchDoc(doc)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium ${activeDoc === doc ? "bg-indigo-600 text-white" : "bg-gray-800 text-gray-400 hover:text-white"}`}>
              {doc === "invoice" ? "Tax invoice" : "Payment receipt"}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-500">{statusText}</span>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <>
          <div className="grid gap-5 lg:grid-cols-2">
            {/* Editor */}
            <div className="rounded-xl border border-gray-800 bg-gray-900 p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-white">HTML / CSS</h2>
                <span className={`text-xs ${overLimit ? "text-red-400" : "text-gray-500"}`}>
                  {sizeKb} KB{maxKb ? ` / ${maxKb} KB` : ""}{isDirty ? " · unsaved" : ""}
                </span>
              </div>
              <textarea
                value={html}
                onChange={(e) => setHtml(e.target.value)}
                onKeyDown={handleTextareaKeyDown}
                spellCheck={false}
                wrap="off"
                className="w-full rounded-lg border border-gray-700 bg-gray-950 p-3 font-mono text-xs text-gray-200 outline-none focus:ring-2 focus:ring-indigo-500"
                style={{ minHeight: "60vh" }}
              />
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" onClick={() => void handlePreview()} disabled={previewing}
                  className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50">
                  {previewing ? "Rendering…" : "Preview PDF"}
                </button>
                <button type="button" onClick={() => void handleSave()} disabled={saving}
                  className="rounded-lg border border-gray-700 px-3 py-2 text-sm text-gray-200 hover:bg-gray-800 disabled:opacity-50">
                  {saving ? "Saving…" : "Save as new version"}
                </button>
                <button type="button" onClick={handleLoadBuiltIn}
                  className="rounded-lg border border-gray-700 px-3 py-2 text-sm text-gray-200 hover:bg-gray-800">
                  Load built-in design
                </button>
                <button type="button" onClick={() => void handleReset()} disabled={resetting}
                  className="rounded-lg border border-red-800/60 px-3 py-2 text-sm text-red-400 hover:bg-red-950/30 disabled:opacity-50">
                  {resetting ? "Resetting…" : "Reset to built-in"}
                </button>
                {isDirty && (
                  <button type="button" onClick={handleDiscard}
                    className="rounded-lg px-3 py-2 text-sm text-gray-400 hover:text-white">
                    Discard changes
                  </button>
                )}
              </div>
            </div>

            {/* Preview */}
            <div className="rounded-xl border border-gray-800 bg-gray-900 p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-white">Preview</h2>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500">Preview with invoice id</label>
                  <input className={`${input} w-40 py-1`} value={previewInvoiceId}
                    onChange={(e) => setPreviewInvoiceId(e.target.value)} placeholder="blank = sample" />
                </div>
              </div>

              {previewErrors.length > 0 && (
                <div className="mb-3 rounded-lg border border-red-800/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
                  <p className="mb-1 font-medium">Fix before saving</p>
                  <ul className="list-inside list-disc space-y-0.5">
                    {previewErrors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
              {previewWarnings.length > 0 && (
                <div className="mb-3 rounded-lg border border-amber-800/60 bg-amber-950/30 px-3 py-2 text-xs text-amber-300">
                  <p className="mb-1 font-medium">Warnings</p>
                  <ul className="list-inside list-disc space-y-0.5">
                    {previewWarnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              {previewUrl ? (
                <iframe title="Invoice preview" src={previewUrl} className="w-full rounded-lg border border-gray-800 bg-white" style={{ minHeight: "55vh" }} />
              ) : (
                <div className="flex items-center justify-center rounded-lg border border-dashed border-gray-800 text-sm text-gray-600" style={{ minHeight: "55vh" }}>
                  Click &ldquo;Preview PDF&rdquo; to render.
                </div>
              )}
            </div>
          </div>

          {/* Placeholders */}
          <div className="mt-5 rounded-xl border border-gray-800 bg-gray-900 p-4">
            <button type="button" onClick={() => setPlaceholdersOpen((o) => !o)} className="text-sm font-semibold text-white">
              Placeholders {placeholdersOpen ? "▲" : "▾"}
            </button>
            {placeholdersOpen && (
              <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {(data?.variables ?? []).map((v) => (
                  <div key={v.placeholder} className="flex items-start justify-between gap-2 rounded-lg bg-gray-800/60 px-3 py-2">
                    <div className="min-w-0">
                      <code className="text-xs text-indigo-300">{v.placeholder}</code>
                      <p className="mt-0.5 text-[11px] text-gray-500">{v.description}</p>
                    </div>
                    <button type="button" onClick={() => void copyPlaceholder(v.placeholder)}
                      className="shrink-0 text-[11px] text-gray-400 hover:text-white">
                      {copiedPlaceholder === v.placeholder ? "Copied" : "Copy"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Versions */}
          <div className="mt-5 overflow-hidden rounded-xl border border-gray-800 bg-gray-900">
            <h2 className="border-b border-gray-800 px-4 py-3 text-sm font-semibold text-white">Versions</h2>
            {versionList.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">No saved versions yet.</p>
            ) : (
              <div className="divide-y divide-gray-800">
                {versionList.map((v) => (
                  <div key={v.version} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-sm text-white">v{v.version}</span>
                        {v.active && <span className="rounded bg-emerald-900/40 px-2 py-0.5 text-xs text-emerald-300">Active</span>}
                      </div>
                      <p className="mt-0.5 truncate text-xs text-gray-500">
                        {v.note || <span className="italic text-gray-600">no note</span>} · saved by {v.created_by} on {formatISTDateTime(v.created_at)}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-3 text-sm">
                      <button type="button" onClick={() => handleLoadVersion(v)} className="text-indigo-400 hover:text-indigo-300">
                        Load into editor
                      </button>
                      {!v.active && (
                        <button type="button" onClick={() => void handleMakeActive(v)} disabled={activating === v.version}
                          className="text-gray-300 hover:text-white disabled:opacity-50">
                          {activating === v.version ? "Activating…" : "Make active"}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
